import { create } from "zustand";
import type { B24Frame } from "@bitrix24/b24jssdk";
import type { BatchOrchestrator } from "../lib/BatchOrchestrator";
import type { ProxyOrchestrator } from "../lib/ProxyOrchestrator";

// ═══════════════════════════════════════════════════════════
// Auth Store — Bitrix24 session context (TPP §2.3)
// ═══════════════════════════════════════════════════════════

export interface CurrentUser {
  id: string;
  fullName: string;
  workPosition: string;
  photo: string | null;
  isAdmin: boolean;
}

interface AuthPayload {
  memberId: string;
  domain: string;
  userId: string;
  isAdmin: boolean;
}

export type { B24Frame };

interface AuthStore {
  memberId: string | null;
  domain: string | null;
  userId: string | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  currentUser: CurrentUser | null;
  b24Frame: B24Frame | null;
  orchestrator: BatchOrchestrator | ProxyOrchestrator | null;
  /** True when running via Standalone Dev Mode (no real B24 iframe). See DEBUGGING.md §5. */
  isStandalone: boolean;
  setAuth: (auth: AuthPayload) => void;
  setCurrentUser: (user: CurrentUser) => void;
  setB24Frame: (frame: B24Frame) => void;
  setOrchestrator: (orchestrator: BatchOrchestrator | ProxyOrchestrator) => void;
  setStandalone: (standalone: boolean) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  memberId: null,
  domain: null,
  userId: null,
  isAdmin: false,
  isAuthenticated: false,
  currentUser: null,
  b24Frame: null,
  orchestrator: null,
  isStandalone: false,

  setAuth: (auth) => {
    set({
      memberId: auth.memberId,
      domain: auth.domain,
      userId: auth.userId,
      isAdmin: auth.isAdmin,
      isAuthenticated: true,
    });
  },

  setCurrentUser: (user) => {
    set({ currentUser: user });
  },

  setB24Frame: (frame) => {
    set({ b24Frame: frame });
  },

  setOrchestrator: (orchestrator) => {
    set({ orchestrator });
  },

  setStandalone: (standalone) => {
    set({ isStandalone: standalone });
  },

  clearAuth: () => {
    set({
      memberId: null,
      domain: null,
      userId: null,
      isAdmin: false,
      isAuthenticated: false,
      currentUser: null,
      b24Frame: null,
      orchestrator: null,
      isStandalone: false,
    });
  },
}));
